import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { getGroupData } from '../../../api/supabase/codeService';

const HOMESCHOOL_DEFAULT_PRICES = {
  kids: {
    one: { original: 917000, discount: 710000, monthly: 59100 },
    two: { original: 1385000, discount: 910000, monthly: 37900 },
  },
  basic: {
    one: { original: 1732000, discount: 1270000, monthly: 105800 },
    two: { original: 2470000, discount: 1770000, monthly: 73700 },
  },
  advanced: {
    one: { original: 774000, discount: 630000, monthly: 52500 },
    two: { original: 1039000, discount: 770000, monthly: 32000 },
  },
  kidsBasic: {
    one: { original: 2409000, discount: 1610000, monthly: 134100 },
    two: { original: 3495000, discount: 2310000, monthly: 96200 },
  },
  basicAdvanced: {
    one: { original: 2266000, discount: 1610000, monthly: 134100 },
    two: { original: 3149000, discount: 2010000, monthly: 83700 },
  },
  allInOne: {
    one: { original: 3093000, discount: 2110000, monthly: 175800 },
    two: { original: 4414000, discount: 2700000, monthly: 112500 },
  },
};

interface HomeschoolPriceData {
  original: number;
  discount: number;
  monthly: number;
}

interface HomeschoolPrices {
  kids: {
    one: HomeschoolPriceData;
    two: HomeschoolPriceData;
  };
  basic: {
    one: HomeschoolPriceData;
    two: HomeschoolPriceData;
  };
  advanced: {
    one: HomeschoolPriceData;
    two: HomeschoolPriceData;
  };
  kidsBasic: {
    one: HomeschoolPriceData;
    two: HomeschoolPriceData;
  };
  basicAdvanced: {
    one: HomeschoolPriceData;
    two: HomeschoolPriceData;
  };
  allInOne: {
    one: HomeschoolPriceData;
    two: HomeschoolPriceData;
  };
}

interface HomeschoolPriceContextType {
  prices: HomeschoolPrices | null;
  isLoading: boolean;
  error: string | null;
}

const HomeschoolPriceContext = createContext<
  HomeschoolPriceContextType | undefined
>(undefined);

// -----------------------------------------------------------------------------
//  캐싱 로직
//  여러 Provider 가 중첩되어도 Supabase 요청이 중복되지 않도록 모듈 레벨에
//  가격 데이터를 보관합니다.
// -----------------------------------------------------------------------------

let cachedPrices: HomeschoolPrices | null = null;
let cachedError: string | null = null;
let cachedFetchPromise: Promise<void> | null = null;

export const HomeschoolPriceProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [prices, setPrices] = useState<HomeschoolPrices | null>(
    HOMESCHOOL_DEFAULT_PRICES,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getPrice = (priceData: Record<string, string> | null, key: string) => {
    return Number(priceData?.[key] || 0);
  };

  useEffect(() => {
    // 이미 캐시가 존재하면 바로 사용하고 종료
    if (cachedPrices) {
      setPrices(cachedPrices);
      setIsLoading(false);
      setError(cachedError);
      return;
    }

    // fetch 가 이미 진행중이면 그 Promise 에 맞춰서 상태 업데이트만 수행
    if (cachedFetchPromise) {
      cachedFetchPromise.then(() => {
        setPrices(cachedPrices);
        setError(cachedError);
        setIsLoading(false);
      });
      return;
    }

    const fetchPrices = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await getGroupData('COURSE');

        const priceData: HomeschoolPrices = {
          kids: {
            one: {
              original: getPrice(res, 'HOMESCHOOL_KIDS_ONE_ORIGINAL'),
              discount: getPrice(res, 'HOMESCHOOL_KIDS_ONE_DISCOUNT'),
              monthly: getPrice(res, 'HOMESCHOOL_KIDS_ONE_MONTHLY'),
            },
            two: {
              original: getPrice(res, 'HOMESCHOOL_KIDS_TWO_ORIGINAL'),
              discount: getPrice(res, 'HOMESCHOOL_KIDS_TWO_DISCOUNT'),
              monthly: getPrice(res, 'HOMESCHOOL_KIDS_TWO_MONTHLY'),
            },
          },
          basic: {
            one: {
              original: getPrice(res, 'HOMESCHOOL_BASIC_ONE_ORIGINAL'),
              discount: getPrice(res, 'HOMESCHOOL_BASIC_ONE_DISCOUNT'),
              monthly: getPrice(res, 'HOMESCHOOL_BASIC_ONE_MONTHLY'),
            },
            two: {
              original: getPrice(res, 'HOMESCHOOL_BASIC_TWO_ORIGINAL'),
              discount: getPrice(res, 'HOMESCHOOL_BASIC_TWO_DISCOUNT'),
              monthly: getPrice(res, 'HOMESCHOOL_BASIC_TWO_MONTHLY'),
            },
          },
          advanced: {
            one: {
              original: getPrice(res, 'HOMESCHOOL_ADV_ONE_ORIGINAL'),
              discount: getPrice(res, 'HOMESCHOOL_ADV_ONE_DISCOUNT'),
              monthly: getPrice(res, 'HOMESCHOOL_ADV_ONE_MONTHLY'),
            },
            two: {
              original: getPrice(res, 'HOMESCHOOL_ADV_TWO_ORIGINAL'),
              discount: getPrice(res, 'HOMESCHOOL_ADV_TWO_DISCOUNT'),
              monthly: getPrice(res, 'HOMESCHOOL_ADV_TWO_MONTHLY'),
            },
          },
          kidsBasic: {
            one: {
              original: getPrice(res, 'HOMESCHOOL_KIDS_BASIC_ONE_ORIGINAL'),
              discount: getPrice(res, 'HOMESCHOOL_KIDS_BASIC_ONE_DISCOUNT'),
              monthly: getPrice(res, 'HOMESCHOOL_KIDS_BASIC_ONE_MONTHLY'),
            },
            two: {
              original: getPrice(res, 'HOMESCHOOL_KIDS_BASIC_TWO_ORIGINAL'),
              discount: getPrice(res, 'HOMESCHOOL_KIDS_BASIC_TWO_DISCOUNT'),
              monthly: getPrice(res, 'HOMESCHOOL_KIDS_BASIC_TWO_MONTHLY'),
            },
          },
          basicAdvanced: {
            one: {
              original: getPrice(res, 'HOMESCHOOL_BASIC_ADV_ONE_ORIGINAL'),
              discount: getPrice(res, 'HOMESCHOOL_BASIC_ADV_ONE_DISCOUNT'),
              monthly: getPrice(res, 'HOMESCHOOL_BASIC_ADV_ONE_MONTHLY'),
            },
            two: {
              original: getPrice(res, 'HOMESCHOOL_BASIC_ADV_TWO_ORIGINAL'),
              discount: getPrice(res, 'HOMESCHOOL_BASIC_ADV_TWO_DISCOUNT'),
              monthly: getPrice(res, 'HOMESCHOOL_BASIC_ADV_TWO_MONTHLY'),
            },
          },
          allInOne: {
            one: {
              original: getPrice(res, 'HOMESCHOOL_ALLINONE_ONE_ORIGINAL'),
              discount: getPrice(res, 'HOMESCHOOL_ALLINONE_ONE_DISCOUNT'),
              monthly: getPrice(res, 'HOMESCHOOL_ALLINONE_ONE_MONTHLY'),
            },
            two: {
              original: getPrice(res, 'HOMESCHOOL_ALLINONE_TWO_ORIGINAL'),
              discount: getPrice(res, 'HOMESCHOOL_ALLINONE_TWO_DISCOUNT'),
              monthly: getPrice(res, 'HOMESCHOOL_ALLINONE_TWO_MONTHLY'),
            },
          },
        };

        // 캐시에 저장
        cachedPrices = priceData;
        cachedError = null;

        setPrices(priceData);
      } catch (error) {
        console.error('COURSE 데이터 조회 실패:', error);
        cachedError = '가격 데이터를 불러오는데 실패했습니다.';
        setError(cachedError);
        setIsLoading(false);
      } finally {
        setIsLoading(false);
        cachedFetchPromise = null; // fetch 완료
      }
    };

    cachedFetchPromise = fetchPrices();
  }, []);

  return (
    <HomeschoolPriceContext.Provider value={{ prices, isLoading, error }}>
      {children}
    </HomeschoolPriceContext.Provider>
  );
};

export const useHomeschoolPrices = () => {
  const context = useContext(HomeschoolPriceContext);
  if (!context) {
    throw new Error(
      'useHomeschoolPrices must be used within a HomeschoolPriceProvider',
    );
  }
  return context;
};
